<template lang="pug">
.waiter-judged-tab(v-cloak)
  .mb-3
  .template-container.ml-2(v-if="value && value.waiter.admissionJudgement")
    .information.mb-2(v-if="value.waiter.admissionJudgement.information" style="white-space: pre-wrap; word-wrap: break-word;") 
      span {{ value.waiter.admissionJudgement.information }}
    .template(v-for="(property, index) in value.waiter.admissionJudgement.templateAnswer.properties")
      WaiterJudgeTemplateItem(v-model="value.waiter.admissionJudgement.templateAnswer.properties[index]" :isChaild="false")
    .mb-3
    .d-flex.flex-row.justify-content-end.mr-3.mb-2.ml-3
      span.point.mr-2 合計
      span.point.mr-2 {{ point }}
      span.point 点
  .template-container(v-else)
    span
</template>

<script lang="ts">
import { Component, Prop, Watch, Vue } from "vue-property-decorator";

import { ResidentGetModel } from "@/commons/services/Residents";

import WaiterJudgeTemplateItem from "@/pc/containers/waiters/WaiterJudgeTemplateItem.vue";
import {
  TemplateItem,
  TemplateItemText,
  TemplateItemRadio,
  TemplateItemRadioGroup,
  TemplateItemSection,
  TemplateType,
  TemplateItemMultipleCheckbox,
  CalcType
} from "@/commons/services/AdmissionJudgementTemplates";

/**
 * ### 待機者詳細タブコンポーネント
 */
@Component({
  components: {
    WaiterJudgeTemplateItem
  }
})
export default class WaiterJudgedTab extends Vue {
  /** 利用者 */
  @Prop()
  value!: ResidentGetModel;

  /** 点数 */
  point: number = 0;

  @Watch("value", { immediate: true })
  public async watchValue(newValue: ResidentGetModel): Promise<void> {
    if (newValue) {
      let totalPoint: number = 0;
      let replacePoint: number | null = null;
      let points: { totalPoint: number; replacePoint: number | null } = { totalPoint, replacePoint };
      this.calculateReplacePoint(newValue.waiter.admissionJudgement.templateAnswer.properties, points);
      this.calculateTotalPoint(newValue.waiter.admissionJudgement.templateAnswer.properties, points);
      if (points.replacePoint != null) {
        this.point = points.replacePoint;
      } else {
        this.point = points.totalPoint;
      }
    }
    return;
  }

  /**
   * ### 「置き換え」の点数計算を行う
   *
   * - 再帰呼び出しして入所判定テンプレートの内容を検索する
   * - 計算種別が「置き換え」のものが存在した場合、置き換え後の点数を引数のreplacePointに格納する
   * - TODO 置き換えが複数存在した場合は何を優先するのか決まっていないので検討が必要かもしれない
   *   - 今は最後に出てきたプロパティで上書きされる
   */
  public calculateReplacePoint(properties: Array<TemplateItem>, points: { totalPoint: number; replacePoint: number | null }): void {
    for (const property of properties) {
      if (property.type == TemplateType.SECTION) {
        const item: TemplateItemSection = property as TemplateItemSection;

        this.calculateReplacePoint(item.properties, points);
      } else if (property.type == TemplateType.RADIO_GROUP) {
        const item: TemplateItemRadioGroup = property as TemplateItemRadioGroup;

        for (const childProperties of item.properties) {
          if (item.answer == childProperties.option.value) {
            this.calculateReplacePoint(childProperties.properties, points);
          }
        }
      } else {
        // テキスト入力
        if (property.type == TemplateType.TEXT) {
          const textItem: TemplateItemText = property as TemplateItemText;
          if (textItem.answer == null) {
            continue;
          }
          if (textItem.calcType == CalcType.REPLACE) {
            points.replacePoint = Number(textItem.answer);
          }
        }
        // ラジオ入力
        else if (property.type == TemplateType.RADIO) {
          const radioItem: TemplateItemRadio = property as TemplateItemRadio;
          if (radioItem.answer == null) {
            continue;
          }
          if (radioItem.calcType == CalcType.REPLACE) {
            for (const option of radioItem.options) {
              if (radioItem.answer == option.value) {
                points.replacePoint = option.point ? Number(option.point) : (option.point as null);
                break;
              }
            }
          }
        }
        // チェックボックス入力
        else if (property.type == TemplateType.MULTIPLE_CHECKBOX) {
          const multipleCheckboxItem: TemplateItemMultipleCheckbox = property as TemplateItemMultipleCheckbox;

          if (multipleCheckboxItem.calcType == CalcType.REPLACE) {
            let point: number | null = null;
            for (let i = 0; i < multipleCheckboxItem.answer.length; i++) {
              if (multipleCheckboxItem.answer[i] == true) {
                if (multipleCheckboxItem.points[i] == null) {
                  continue;
                }
                if (point == null || point < Number(multipleCheckboxItem.points[i])) {
                  point = Number(multipleCheckboxItem.points[i]);
                }
              }
            }

            points.replacePoint = point != null ? point : null;
          }
        }
      }
    }
    return;
  }

  /**
   * ### 「加算」の点数計算を行う
   *
   * - 再帰呼び出しして入所判定テンプレートの内容を検索する
   * - 計算種別が「加算」のものが存在した場合、点数をtotalPointに加算する
   */
  public calculateTotalPoint(properties: Array<TemplateItem>, points: { totalPoint: number; replacePoint: number | null }): void {
    for (const property of properties) {
      if (property.type == TemplateType.SECTION) {
        const item: TemplateItemSection = property as TemplateItemSection;

        this.calculateTotalPoint(item.properties, points);
      } else if (property.type == TemplateType.RADIO_GROUP) {
        const item: TemplateItemRadioGroup = property as TemplateItemRadioGroup;

        for (const childProperties of item.properties) {
          if (item.answer == childProperties.option.value) {
            this.calculateTotalPoint(childProperties.properties, points);
          }
        }
      } else {
        // テキスト入力
        if (property.type == TemplateType.TEXT) {
          const textItem: TemplateItemText = property as TemplateItemText;
          if (textItem.answer == null) {
            continue;
          }
          if (textItem.calcType == CalcType.ADD || textItem.calcType == CalcType.CALC_ADD) {
            points.totalPoint += Number(textItem.answer);
          }
        }
        // ラジオ入力
        else if (property.type == TemplateType.RADIO) {
          const radioItem: TemplateItemRadio = property as TemplateItemRadio;
          if (radioItem.answer == null) {
            continue;
          }
          if (radioItem.calcType == CalcType.ADD || radioItem.calcType == CalcType.CALC_ADD) {
            for (const option of radioItem.options) {
              if (radioItem.answer == option.value) {
                points.totalPoint += Number(option.point);
              }
            }
          }
        }
        // チェックボックス入力
        else if (property.type == TemplateType.MULTIPLE_CHECKBOX) {
          const multipleCheckboxItem: TemplateItemMultipleCheckbox = property as TemplateItemMultipleCheckbox;

          if (multipleCheckboxItem.calcType == CalcType.ADD || multipleCheckboxItem.calcType == CalcType.CALC_ADD) {
            let point: number = 0;

            for (let i = 0; i < multipleCheckboxItem.answer.length; i++) {
              if (multipleCheckboxItem.answer[i] == true) {
                if (multipleCheckboxItem.points[i] == null) {
                  continue;
                }

                // 計算種別：加算の場合は選択肢で最高得点を採用する
                if (multipleCheckboxItem.calcType == CalcType.ADD) {
                  if (point < Number(multipleCheckboxItem.points[i])) {
                    point = Number(multipleCheckboxItem.points[i]);
                  }
                }
                // 計算種別：計算加算の場合は選択されたpointを合算する
                else if (multipleCheckboxItem.calcType == CalcType.CALC_ADD) {
                  point += Number(multipleCheckboxItem.points[i]);
                }
              }
            }

            // 計算種別：計算加算の場合は上限までの点数とする
            if (multipleCheckboxItem.calcType == CalcType.CALC_ADD && multipleCheckboxItem.limitPoint != null && multipleCheckboxItem.limitPoint != undefined) {
              if (multipleCheckboxItem.limitPoint < point) {
                point = multipleCheckboxItem.limitPoint;
              }
            }

            points.totalPoint += point;
          }
        }
      }
    }
    return;
  }

  /**
   * ### vue lifecycle created
   */
  public async mounted(): Promise<void> {
    return;
  }
}
</script>

<style lang="scss" scoped>
.waiter-judged-tab {
  position: relative;
  width: 100%;
  min-height: 240px;

  .point {
    font-weight: bold;
    font-size: 20px;
  }
}
</style>
